import React, { useContext, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ShieldCheck, ArrowLeft } from 'lucide-react';

export default function LoginPage() {
  const { login, complete2FA } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(form.email, form.password);
      if (result.requires_2fa) {
        setTempToken(result.temp_token);
        setShow2FA(true);
        setLoading(false);
        return;
      }
      toast.success('Logged in successfully');
      navigate(from, { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Invalid credentials';
      if (status === 423) {
        toast.error(detail, { duration: 8000 });
      } else if (status === 429) {
        toast.error('Too many login attempts. Please wait a minute and try again.', { duration: 6000 });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await complete2FA(tempToken, totpCode);
      if (result.backup_code_used) {
        toast.success(`Logged in with backup code (${result.backup_codes_remaining} remaining)`);
      } else {
        toast.success('Logged in successfully');
      }
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid 2FA code');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  if (show2FA) {
    return (
      <Layout>
        <div className="min-h-[70vh] flex items-center justify-center px-4 py-16" data-testid="2fa-page">
          <div className="w-full max-w-md">
            <div className="bg-card border border-border rounded-sm p-8">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-[#78BE20]/10 rounded-full flex items-center justify-center">
                  <ShieldCheck className="h-6 w-6 text-[#78BE20]" />
                </div>
              </div>
              <h1 className="text-2xl font-black text-foreground uppercase tracking-tight text-center mb-2">
                Two-Factor Authentication
              </h1>
              <p className="text-sm text-muted-foreground text-center mb-6">
                Enter the 6-digit code from your authenticator app, or a backup code.
              </p>

              <form onSubmit={handle2FASubmit} className="space-y-4">
                <div>
                  <Label htmlFor="totp-code">Authentication Code</Label>
                  <Input
                    id="totp-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={8}
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\s/g, ''))}
                    className="mt-1 bg-background border-border text-center text-2xl tracking-[0.5em] font-mono"
                    required
                    data-testid="2fa-code-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || totpCode.length < 6}
                  className="w-full bg-[#78BE20] text-black hover:bg-[#65a31b] h-12"
                  data-testid="2fa-submit"
                >
                  {loading ? 'Verifying...' : 'Verify'}
                </Button>
              </form>

              <button
                onClick={() => { setShow2FA(false); setTotpCode(''); setTempToken(''); }}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mt-4 mx-auto"
                data-testid="2fa-back-btn"
              >
                <ArrowLeft className="h-3 w-3" /> Back to login
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16" data-testid="login-page">
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-sm p-8">
            <h1 className="text-2xl font-black text-foreground uppercase tracking-tight text-center mb-6">
              Login
            </h1>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 bg-background border-border"
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="bg-background border-border pr-10"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-sm text-[#78BE20] hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#78BE20] text-black hover:bg-[#65a31b] h-12"
                data-testid="login-submit"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>

            <p className="text-center text-muted-foreground text-sm mt-6">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#78BE20] hover:underline">
                Register
              </Link>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
